/**
 * DataFilesGuide — Comprehensive user guide for the Data Files module.
 * Covers all master data types: Categories, Currencies, Suppliers, Items, etc.
 */
import React from 'react';
import GuideViewer, { GuideSection, GuideSubSection, GuideCallout, GuideFieldTable, GuideTOC } from './GuideViewer';
import AppShell from '../../components/layout/AppShell';

function GuideContent() {
  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'common-features', label: 'Common Features' },
    { id: 'category-codes', label: 'Category Codes' },
    { id: 'currency-codes', label: 'Currency Codes' },
    { id: 'delivery-points', label: 'Delivery Points' },
    { id: 'document-types', label: 'Document Types' },
    { id: 'invoice-points', label: 'Invoice Points' },
    { id: 'items', label: 'Items (Products & Services)' },
    { id: 'suppliers', label: 'Suppliers' },
    { id: 'customers', label: 'Customers' },
    { id: 'terms', label: 'Terms & Conditions' },
    { id: 'group-items', label: 'Group Items' },
    { id: 'reports', label: 'Data File Reports' },
  ];

  return (
    <GuideViewer title="Data Files Module" subtitle="Master Data Management User Guide">
      <GuideTOC items={toc} />

      <GuideSection id="overview" title="1. Overview">
        <p className="text-sm text-slate-600 mb-3">
          Data Files are the foundation of your Eyvo system. They contain all the reference (master) data that is used across procurement transactions. Before you can create requisitions, purchase orders, or sourcing events, your Data Files must be properly configured.
        </p>
        <p className="text-sm text-slate-600 mb-3">
          Data Files are accessed from the <strong>Data Files</strong> menu in the left sidebar. Click the menu to expand it and see all available data types.
        </p>
        <GuideCallout type="info" title="Who Can Manage Data Files?">
          <p>Access to each data file type is controlled by user permissions. Your system administrator assigns read/write access per data file type. If you cannot see or edit a data file, contact your administrator.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="common-features" title="2. Common Features Across All Data Files">
        <GuideSubSection title="Interactive Table">
          <p className="text-sm text-slate-600 mb-2">
            Every data file listing uses the <strong>Interactive Table</strong> component which provides:
          </p>
          <GuideFieldTable fields={[
            { name: 'Sortable Columns', desc: 'Click any column header to sort ascending or descending. A small arrow indicator shows the current sort direction.' },
            { name: 'Resizable Columns', desc: 'Drag the right edge of any column header to resize it. Your column widths are preserved for the session.' },
            { name: 'Inline Column Filters', desc: 'Click the "Filters" button in the toolbar to reveal a row of per-column search inputs below the headers. Type in any column filter to instantly narrow results.' },
            { name: 'Column Picker', desc: 'Click the gear icon to show/hide columns or drag them to reorder. Not all columns are visible by default.' },
            { name: 'Show Addresses', desc: 'For entities with address data (Suppliers, Delivery Points, Invoice Points, Customers), a "Show Addresses" checkbox toggles address columns on/off.' },
            { name: 'Global Search', desc: 'The search bar above the table filters across all visible columns simultaneously.' },
            { name: 'Pagination', desc: 'Navigate between pages using the pagination bar. Change the page size (10, 25, 50, 100) from the dropdown.' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Adding & Editing Records">
          <p className="text-sm text-slate-600 mb-2">
            Most data files have a <strong>+ New</strong> button to create a new record. Click an existing record in the table to open it for editing. Depending on your permissions, you can modify fields and click <strong>Save</strong>.
          </p>
        </GuideSubSection>
        <GuideSubSection title="Reports Tab">
          <p className="text-sm text-slate-600 mb-2">
            Each data file page has a <strong>Reports</strong> tab that shows available reports. Reports allow you to:
          </p>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
            <li>Apply advanced filters (date ranges, specific codes, etc.)</li>
            <li>Export to <strong>CSV</strong> (all columns, not just visible ones)</li>
            <li>Export to <strong>PDF</strong> with Eyvo branding</li>
            <li><strong>Email</strong> a PDF directly to stakeholders</li>
            <li><strong>Schedule</strong> reports for automatic recurring delivery</li>
          </ul>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="category-codes" title="3. Category Codes">
        <p className="text-sm text-slate-600 mb-3">
          Category Codes provide a hierarchical classification system for organising your procurement spend. Categories can be nested (parent-child relationships) to create a tree structure, for example: "IT" &gt; "Hardware" &gt; "Laptops".
        </p>
        <GuideSubSection title="Screen Elements">
          <GuideFieldTable fields={[
            { name: 'Code', desc: 'A unique identifier for the category (e.g., "IT-HW-LAP"). This is typically alphanumeric.' },
            { name: 'Description', desc: 'A human-readable name for the category (e.g., "Laptop Computers").' },
            { name: 'Parent Code', desc: 'If this is a sub-category, this field links it to its parent category in the hierarchy.' },
            { name: 'Active', desc: 'Whether the category is currently available for selection on transactions. Inactive categories are hidden from dropdown lists.' },
          ]} />
        </GuideSubSection>
        <GuideCallout type="tip" title="Best Practice">
          <p>Create a well-organised category hierarchy before you begin transacting. Categories are used for spend analysis, reporting, and supplier classification.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="currency-codes" title="4. Currency Codes">
        <p className="text-sm text-slate-600 mb-3">
          Currency Codes define the currencies your organisation uses for procurement transactions. One currency is designated as the <strong>Base Currency</strong> (typically your home currency), and all other currencies have exchange rates relative to it.
        </p>
        <GuideSubSection title="Screen Elements">
          <GuideFieldTable fields={[
            { name: 'Code', desc: 'The ISO 4217 three-letter currency code (e.g., USD, GBP, EUR, JPY).' },
            { name: 'Description', desc: 'The full name of the currency (e.g., "US Dollar", "British Pound Sterling").' },
            { name: 'Exchange Rate', desc: 'The conversion rate relative to your base currency. For example, if USD is base, GBP might show 0.79.' },
            { name: 'Base Currency', desc: 'Indicates if this is your organisation\'s base (home) currency. Only one currency can be the base.' },
            { name: 'Active', desc: 'Whether the currency is available for selection on transactions.' },
            { name: 'Auto Sync', desc: 'If enabled, exchange rates are automatically updated from an external rate source (ExchangeRate-API).' },
            { name: 'Last Updated', desc: 'The date and time when this currency\'s exchange rate was last synced, displayed in your system timezone.' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Rate Source & Sync">
          <p className="text-sm text-slate-600 mb-2">
            When viewing the Currencies report, a summary card shows the <strong>Rate Source</strong> (e.g., "ExchangeRate-API") and the <strong>Last Updated</strong> timestamp. The "Last Updated" column header includes the timezone abbreviation (e.g., "Last Updated (PDT)") so you always know the exact time context of the rate data.
          </p>
          <GuideCallout type="info" title="Automatic Rate Updates">
            <p>Currencies with "Auto Sync" enabled will have their rates refreshed periodically from the configured rate provider. Manual rates (Auto Sync disabled) must be updated by an administrator.</p>
          </GuideCallout>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="delivery-points" title="5. Delivery Points">
        <p className="text-sm text-slate-600 mb-3">
          Delivery Points represent physical locations where goods can be delivered. They are selected on purchase orders to tell suppliers where to ship.
        </p>
        <GuideSubSection title="Screen Elements">
          <GuideFieldTable fields={[
            { name: 'Code', desc: 'A unique identifier for the delivery point (e.g., "DP-WAREHOUSE-01").' },
            { name: 'Name', desc: 'The name of the delivery location (e.g., "Main Warehouse, Building A").' },
            { name: 'Address Fields', desc: 'Street address, city, state/province, zip/postal code, and country. Toggle "Show Addresses" to see these columns.' },
            { name: 'Contact Name', desc: 'The primary contact person at this delivery location.' },
            { name: 'Telephone / Email', desc: 'Contact details for delivery coordination.' },
            { name: 'Active', desc: 'Whether the delivery point is available for selection on transactions.' },
          ]} />
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="document-types" title="6. Document Types">
        <p className="text-sm text-slate-600 mb-3">
          Document Types categorise your purchase requisitions and orders (e.g., "Standard Purchase", "Urgent Requirement", "Capital Expenditure"). They can have associated budgets and are used for reporting and workflow routing.
        </p>
        <GuideSubSection title="Screen Elements">
          <GuideFieldTable fields={[
            { name: 'Code', desc: 'A short identifier for the document type.' },
            { name: 'Description', desc: 'Full name of the document type.' },
            { name: 'Active', desc: 'Whether the document type is available for selection.' },
            { name: 'Budget', desc: 'An optional budget amount associated with this document type for spend tracking.' },
          ]} />
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="invoice-points" title="7. Invoice Points">
        <p className="text-sm text-slate-600 mb-3">
          Invoice Points represent the billing addresses within your organisation. They tell suppliers where to send invoices. Each invoice point has its own address and contact details.
        </p>
        <p className="text-sm text-slate-600">
          The fields mirror Delivery Points (Code, Name, Address, Contact, Active) but are used specifically for the invoicing/billing address on purchase orders and invoices.
        </p>
      </GuideSection>

      <GuideSection id="items" title="8. Items (Products & Services)">
        <p className="text-sm text-slate-600 mb-3">
          Items are the products and services your organisation purchases. Maintaining an item master allows standardised descriptions, pricing, and supplier linkages across all transactions.
        </p>
        <GuideSubSection title="Screen Elements">
          <GuideFieldTable fields={[
            { name: 'Item Code', desc: 'A unique SKU or product code (e.g., "LAP-DELL-5520").' },
            { name: 'Description', desc: 'A detailed description of the item.' },
            { name: 'Outline', desc: 'A shorter summary description used in list views.' },
            { name: 'Unit of Measure', desc: 'The standard unit (EACH, BOX, KG, LITRE, etc.).' },
            { name: 'Unit Price', desc: 'The current standard price for this item.' },
            { name: 'Category Code', desc: 'Links the item to a spend category.' },
            { name: 'Supplier Code', desc: 'The preferred supplier for this item.' },
            { name: 'Active', desc: 'Whether the item is available for selection on transactions.' },
            { name: 'Track Inventory', desc: 'Whether this item is stock-managed in the Inventory module.' },
            { name: 'Barcode', desc: 'An optional barcode/QR code for warehouse scanning.' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Price History">
          <p className="text-sm text-slate-600 mb-2">
            The Item detail view includes a <strong>Price History</strong> tab showing every price change over time, including the old price, new price, percentage change, change type (manual, from order, etc.), who made the change, and when.
          </p>
        </GuideSubSection>
      </GuideSection>

      <GuideSection id="suppliers" title="9. Suppliers">
        <p className="text-sm text-slate-600 mb-3">
          The Supplier master is one of the most important data files. It contains all the information about the companies you purchase from, including contact details, compliance documents, ESG ratings, and performance history.
        </p>
        <GuideSubSection title="Listing Screen">
          <GuideFieldTable fields={[
            { name: 'Supplier Code', desc: 'A unique identifier assigned to each supplier.' },
            { name: 'Name', desc: 'The registered company name.' },
            { name: 'Contact', desc: 'The primary contact person.' },
            { name: 'Email / Telephone', desc: 'Contact details for the supplier.' },
            { name: 'City / Country', desc: 'The supplier\'s location. Use "Show Addresses" for full address columns.' },
            { name: 'Active', desc: 'Whether the supplier is available for transactions.' },
            { name: 'Status', desc: 'Approval status (Approved, Pending, Blocked, etc.).' },
          ]} />
        </GuideSubSection>
        <GuideSubSection title="Supplier Detail Tabs">
          <p className="text-sm text-slate-600 mb-2">
            Clicking a supplier opens a multi-tab detail view:
          </p>
          <GuideFieldTable fields={[
            { name: 'Details', desc: 'Core supplier information — name, addresses, contacts, banking details, tax information.' },
            { name: 'Documents', desc: 'Upload and manage compliance documents (certificates, insurance, etc.) with expiry tracking.' },
            { name: 'Contacts', desc: 'Manage multiple contact persons with their roles, emails, and phone numbers.' },
            { name: 'Performance', desc: 'View delivery performance metrics (on-time %, average delay).' },
            { name: 'ESG / Sustainability', desc: 'Environmental, Social, and Governance ratings with AI-assisted lookup.' },
            { name: 'Change History', desc: 'An audit trail of all changes made to the supplier record, with timestamps and user details.' },
          ]} />
        </GuideSubSection>
        <GuideCallout type="tip" title="AI-Assisted Supplier Lookup">
          <p>When creating a new supplier, the AI lookup feature can automatically retrieve company details, ESG ratings, and compliance information from public sources. Look for the "AI Lookup" button on the supplier form.</p>
        </GuideCallout>
      </GuideSection>

      <GuideSection id="customers" title="10. Customers">
        <p className="text-sm text-slate-600 mb-3">
          If your organisation sells as well as buys, the Customers data file maintains your customer master. Customers are used on Sales Orders.
        </p>
        <GuideFieldTable fields={[
          { name: 'Customer Code', desc: 'Unique identifier for the customer.' },
          { name: 'Customer Name', desc: 'The registered company or individual name.' },
          { name: 'Type', desc: 'Customer classification (Corporate, Government, Individual, etc.).' },
          { name: 'Currency', desc: 'The preferred transaction currency for this customer.' },
          { name: 'Payment Terms', desc: 'Standard payment terms (e.g., Net 30, Net 60).' },
          { name: 'Active', desc: 'Whether the customer is available for sales transactions.' },
        ]} />
      </GuideSection>

      <GuideSection id="terms" title="11. Terms & Conditions">
        <p className="text-sm text-slate-600 mb-3">
          Terms & Conditions are pre-written legal clauses that can be attached to purchase orders, contracts, and sourcing events. They are maintained centrally so all documents use consistent, approved language.
        </p>
        <GuideFieldTable fields={[
          { name: 'Code', desc: 'A short identifier for the clause (e.g., "T-PAYMENT-30").' },
          { name: 'Description', desc: 'The title or category of the clause.' },
          { name: 'Full Text', desc: 'The complete legal text of the clause.' },
          { name: 'Active', desc: 'Whether the clause is available for selection.' },
        ]} />
      </GuideSection>

      <GuideSection id="group-items" title="12. Group Items">
        <p className="text-sm text-slate-600 mb-3">
          Group Items allow you to bundle multiple related items together. When a group item is added to a requisition or order, all the component items are automatically added as individual line items. This is useful for standard kits, assemblies, or recurring order bundles.
        </p>
      </GuideSection>

      <GuideSection id="reports" title="13. Data File Reports">
        <p className="text-sm text-slate-600 mb-3">
          Each data file type has associated reports accessible from the <strong>Reports</strong> tab on the listing page. Data file reports pull all records with their complete field sets and support:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 mb-3">
          <li><strong>Filtering</strong> — Apply criteria specific to the data type (code, name, city, active status, etc.)</li>
          <li><strong>Re-Run</strong> — Refresh the data with current filters</li>
          <li><strong>CSV Export</strong> — Downloads ALL database columns (not just visible ones) for complete data extraction</li>
          <li><strong>PDF Export</strong> — Professionally formatted with Eyvo branding</li>
          <li><strong>Email</strong> — Generate and email a PDF directly</li>
          <li><strong>Schedule</strong> — Set up automated recurring report delivery</li>
        </ul>
        <GuideCallout type="info" title="Full Data Export">
          <p>The CSV export from Data File reports includes every user-facing field in the database, even columns not currently displayed in the table. This ensures complete data extraction for external analysis.</p>
        </GuideCallout>
      </GuideSection>
    </GuideViewer>
  );
}

export default function DataFilesGuidePage() {
  return <AppShell><GuideContent /></AppShell>;
}

export function DataFilesGuideEmbed() {
  return <GuideContent />;
}
